import { Link, useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { useAuth, useCan } from "../../auth/AuthProvider";
import {
  Alert,
  Card,
  CardHeader,
  EmptyState,
  LoadingBlock,
  Mono,
  Table,
  Td,
  Th,
  buttonStyles,
} from "../../components/ui";
import { nextRun } from "../../lib/cron";
import { relativeTime } from "../../lib/format";
import { useSchedules } from "../../lib/queries";

/**
 * One app's scheduled jobs.
 *
 * The same query the org-wide Schedules page runs, filtered — so opening this
 * tab after visiting that page is served from cache, and a mutation on either
 * invalidates both.
 */
export function SchedulesTab() {
  const { t } = useTranslation("app");
  /* The button is the schedules feature's own, and says the same words there. */
  const { t: scheduleText } = useTranslation("schedules");
  const { appId } = useParams();
  const { currentOrg } = useAuth();
  const canManage = useCan("developer");

  const schedules = useSchedules(currentOrg?.id, appId);
  const rows = schedules.data?.data ?? [];

  if (schedules.isPending) return <LoadingBlock />;
  if (schedules.error) return <Alert>{schedules.error.message}</Alert>;

  // Only provisioned jobs are billed, so only they are summed.
  const billedCents = rows
    .filter((job) => job.provisioned)
    .reduce((total, job) => total + job.price.monthlyCents, 0);

  return (
    <div className="space-y-4">
      {rows.length === 0 ? (
        <Card>
          <EmptyState
            icon="clock"
            title={t("schedules.empty")}
            description={t("schedules.emptyDescription")}
            action={
              canManage ? (
                <Link
                  to={`/schedules/new?appId=${appId ?? ""}`}
                  className={buttonStyles("primary")}
                >
                  {scheduleText("create")}
                </Link>
              ) : undefined
            }
          />
        </Card>
      ) : (
        <Card>
          <CardHeader
            title={t("schedules.title")}
            description={t("schedules.summary", {
              count: rows.length,
              amount: `$${(billedCents / 100).toFixed(2)}`,
            })}
            action={
              canManage ? (
                <Link
                  to={`/schedules/new?appId=${appId ?? ""}`}
                  className={buttonStyles("secondary", "sm")}
                >
                  {scheduleText("create")}
                </Link>
              ) : undefined
            }
          />
          <Table
            head={
              <>
                <Th>{t("schedules.job")}</Th>
                <Th>{t("schedules.schedule")}</Th>
                <Th priority="sm">{t("schedules.nextRun")}</Th>
                <Th priority="lg">{t("schedules.command")}</Th>
                <Th className="text-right">{t("schedules.cost")}</Th>
              </>
            }
          >
            {rows.map((job) => {
              const upcoming =
                job.enabled && job.provisioned ? nextRun(job.cronExpression, job.timezone) : null;
              return (
                <tr key={job.id} className="transition-colors hover:bg-raised/50">
                  <Td>
                    <Link
                      to={`/schedules/${job.id}`}
                      className="font-medium text-fg hover:underline"
                    >
                      {job.name}
                    </Link>
                    {(!job.enabled || !job.provisioned) && (
                      <div className="mt-1">
                        {!job.provisioned ? (
                          <span className="rounded-md border border-danger/30 bg-danger/8 px-1.5 py-0.5 text-[11px] text-danger">
                            {t("schedules.notCreated")}
                          </span>
                        ) : (
                          <span className="rounded-md border border-line bg-raised px-1.5 py-0.5 text-[11px] text-fg-subtle">
                            {t("schedules.paused")}
                          </span>
                        )}
                      </div>
                    )}
                  </Td>
                  <Td>
                    <div className="text-fg">{job.cronDescription}</div>
                    <div className="mt-0.5 text-[11px] text-fg-subtle">{job.timezone}</div>
                  </Td>
                  <Td priority="sm" className="text-fg-muted">
                    {upcoming ? (
                      relativeTime(upcoming.toISOString())
                    ) : (
                      <span className="text-fg-subtle">—</span>
                    )}
                  </Td>
                  <Td priority="lg">
                    <Mono className="text-[12px] text-fg-muted">
                      {job.command.length > 40 ? `${job.command.slice(0, 40)}…` : job.command}
                    </Mono>
                  </Td>
                  <Td className="text-right tabular-nums text-fg-muted">
                    {job.provisioned ? (
                      t("schedules.perMonth", {
                        amount: `$${(job.price.monthlyCents / 100).toFixed(2)}`,
                      })
                    ) : (
                      <span className="text-fg-subtle">{t("schedules.notBilled")}</span>
                    )}
                  </Td>
                </tr>
              );
            })}
          </Table>
        </Card>
      )}
    </div>
  );
}
